import type { Campaign, CreateCampaignRequest, Fund, NavCampaignConfig } from '../webapp/src/types';

const channels = ['sms', 'email', 'whatsapp', 'customEmail', 'notification'];
const payloadFields = new Set([
    'title', 'type', 'customerRecipients', 'tagsRecipients', 'segmentsRecipients',
    'rmRecipients', 'rmRecipientRoles', 'messageVariables', 'updateLink',
    'smsMessage', 'emailSubject', 'emailMessage', 'selectedWhatsappTemplate',
    'selectedEmailTemplate', 'selectedSMSTemplate', 'selectedNotificationTemplate',
    'selectedCustomEmailTemplate', 'notificationTitle', 'notificationBody',
    'notificationData', 'notificationDataLink',
]);

/** Discard transient form controls and undefined optional values before saving. */
export function navCampaignPayload(values: NavCampaignConfig['payload']): NavCampaignConfig['payload'] {
    return JSON.parse(JSON.stringify(Object.fromEntries(Object.entries(values).filter(([key]) => payloadFields.has(key)))));
}

/** NAV customer audiences always resolve from the fund's own tag in Typesense. */
export function navCampaignAudience(fundID: string | undefined): Pick<CreateCampaignRequest, 'customerRecipients' | 'tagsRecipients' | 'segmentsRecipients'> {
    if (typeof fundID !== 'string' || !fundID.trim()) throw new Error('A fund ID is required for the NAV campaign audience.');
    return { customerRecipients: [], tagsRecipients: [fundID], segmentsRecipients: [] };
}

export function validateNavCampaignConfig(config: NavCampaignConfig): void {
    if (!config || !['active', 'inactive'].includes(config.status) || typeof config.sendAutomatically !== 'boolean') {
        throw new Error('Choose a campaign status and whether to send automatically.');
    }
    const p = config.payload;
    if (!p || typeof p !== 'object' || Object.keys(p).some(key => !payloadFields.has(key))) {
        throw new Error('Invalid NAV campaign payload. Store campaign creation fields only.');
    }
    if (typeof p.title !== 'string' || !p.title.trim()) throw new Error('Campaign title is required.');
    for (const field of ['smsMessage', 'emailSubject', 'emailMessage', 'selectedEmailTemplate', 'notificationTitle', 'notificationBody'] as const) {
        if (p[field] !== undefined && typeof p[field] !== 'string') throw new Error(`${field} must contain text.`);
    }
    if (!Array.isArray(p.type) || !p.type.length || p.type.some(type => !channels.includes(type))) {
        throw new Error('Choose at least one valid message channel.');
    }
    if (!Array.isArray(p.rmRecipients) || p.rmRecipients.some(value => typeof value !== 'string' || !value.trim())) {
        throw new Error('Invalid rmRecipients.');
    }
    if (p.rmRecipients.length && (!p.rmRecipientRoles?.length || p.rmRecipientRoles.some(role => !['rm', 'operations'].includes(role)))) {
        throw new Error('Choose RM recipient roles.');
    }
    const required: Partial<Record<CreateCampaignRequest['type'][number], (keyof CreateCampaignRequest)[]>> = {
        sms: ['smsMessage'], email: ['selectedEmailTemplate'], whatsapp: ['selectedWhatsappTemplate'],
        customEmail: ['emailSubject', 'emailMessage'], notification: ['notificationTitle', 'notificationBody'],
    };
    for (const channel of p.type) {
        for (const field of required[channel] || []) {
            const value = p[field];
            if (!value || (typeof value === 'string' && !value.trim())) throw new Error(`${field} is required.`);
        }
    }
    if (p.type.includes('whatsapp') && !p.selectedWhatsappTemplate?.sid) throw new Error('Choose a WhatsApp template.');
    if (!p.messageVariables || typeof p.messageVariables !== 'object' || Array.isArray(p.messageVariables) ||
        Object.values(p.messageVariables).some(value => typeof value !== 'string')) {
        throw new Error('Message variables must contain text values.');
    }
}

function navDay(fund?: Partial<Fund>): string | undefined {
    const date = fund?.nav_date?.toDate();
    return date && Number.isFinite(date.getTime()) ? date.toISOString().slice(0, 10) : undefined;
}

export function isNavCampaignUpdate(before: Partial<Fund> | undefined, after: Partial<Fund>): boolean {
    if (!before || after.navCampaign?.status !== 'active' || !Number.isFinite(after.nav_per_unit) ||
        !after.nav_per_unit || after.nav_per_unit < 0 || !navDay(after)) return false;
    return !Number.isFinite(before.nav_per_unit) ||
        Math.abs(after.nav_per_unit - (before.nav_per_unit || 0)) >= 1e-10 || navDay(before) !== navDay(after);
}

/** Resolve only fund placeholders; customer placeholders remain for delivery time. */
export function renderNavCampaignPayload(config: NavCampaignConfig, fund: Partial<Fund>): CreateCampaignRequest {
    validateNavCampaignConfig(config);
    const render = (text: string): string => text.replace(/{{\s*fund\.([\w.]+)\s*}}/g, (_, path: string) => {
        let value: any = path.split('.').reduce((obj: any, key: string) => obj?.[key], fund);
        if (value?.toDate) value = value.toDate().toISOString().slice(0, 10).split('-').reverse().join('-');
        if (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean') {
            throw new Error(`Fund field ${path} is unavailable for the NAV campaign.`);
        }
        if (typeof value === 'number' && !Number.isFinite(value)) throw new Error(`Invalid fund field ${path}.`);
        return String(value);
    });
    const payload = { ...config.payload, ...navCampaignAudience(fund.id), messageVariables: { ...config.payload.messageVariables } };
    for (const field of ['title', 'smsMessage', 'emailSubject', 'emailMessage', 'notificationTitle', 'notificationBody'] as const) {
        if (payload[field]) payload[field] = render(payload[field]!);
    }
    for (const [key, value] of Object.entries(payload.messageVariables)) payload.messageVariables[key] = render(value);
    return payload;
}

/** Same delivery shape used by staff-run campaigns. */
export function prepareCampaignChannels(campaign: Campaign, customers: number, rms: number): Partial<Campaign> {
    const totals = { message_total: customers, message_sent: 0, message_failed: 0, errors: [],
        rm_message_total: rms, rm_message_sent: 0, rm_message_failed: 0, rm_errors: [] };
    const result: Partial<Campaign> = {};
    if (campaign.type.includes('sms')) result.sms = { ...totals, message: campaign.smsMessage! };
    if (campaign.type.includes('email')) result.email = { ...totals, template: campaign.selectedEmailTemplate! };
    if (campaign.type.includes('whatsapp')) result.whatsapp = { ...totals, template: campaign.selectedWhatsappTemplate! };
    if (campaign.type.includes('customEmail')) result.custom_email = { ...totals, subject: campaign.emailSubject!, message: campaign.emailMessage! };
    if (campaign.type.includes('notification')) result.notification = { ...totals, title: campaign.notificationTitle!, body: campaign.notificationBody!,
        ...(campaign.notificationData ? { data: campaign.notificationData } : {}) };
    return result;
}
